import { isDate, isDefined, isString } from 'class-validator';
import { isBoolean, isNil, isNumber, sortBy } from 'lodash';
import { DateTime, DateTimeOptions, DurationUnits } from 'luxon';
import strip from 'striptags';

import {
  AppointmentCustomFormExportRow,
  AppointmentMetadataEnum,
  getReschedulesCount,
  IAppointment
} from './appointment';
import { DatabaseEntities } from './base';
import { formatDateTimeWithMilitarySupport, LuxonDateTimeFormats } from './chronon';
import { CustomFieldType, getDocumentNameFromUrl, type ICustomField } from './custom-field';
import { CustomFormsFeatures, parseGridTileLoadTypeCustomFormFieldId } from './custom-forms';
import type { Maybe, TypeormEntity } from './globals';
import { NovaDate } from './nova-date';
import { ExportableFields, getExportableFieldLabel } from './reporting';
import { EntitySettings, ISettings, makeEntitySettings } from './settings';
import { AppointmentStatus } from './status';
import { isCarrierUser, IUser } from './user';

type ExcelHyperlinkCell = {
  t: string;
  v: string;
  l: { Target: string; Tooltip: string };
};

export type ExportAppointmentRowValue = string | number | ExcelHyperlinkCell;

type ExportableFieldKey = keyof typeof ExportableFields;

/** Appointment rows as loaded for Excel export (relations beyond `IAppointment`). */
export type IAppointmentForExport = IAppointment & {
  loadType?: { name?: string; direction?: string };
  /** Carrier / contact row may include nested company when loaded for export */
  user?: IUser & { company?: { name?: string } };
  createdByUser?: IUser;
  lastChangedByUser?: IUser;
};

export class ExportAppointment {
  constructor(
    public appointments: IAppointmentForExport[],
    public settings: ISettings = {},
    public exportableFields: string[],
    public locale: string | null = null
  ) {
    if (!isDefined(settings)) {
      this.settings = {};
    }
  }

  public stripNotesField(notes: Maybe<string>): string {
    if (!notes || !isString(notes)) {
      return '';
    }

    // Replace <br> with newline character
    notes = notes.replace(/<br\s*\/?>/giu, '\n');
    // Replace &nbsp; with space
    notes = notes.replace(/&nbsp;/gu, ' ');
    // Replace paragraph tags with their content plus a line break
    notes = notes.replace(/<\/p>/giu, '\n');
    return strip(notes.trim());
  }

  public convertAppointmentsToExcelData(): Record<string, ExportAppointmentRowValue>[] {
    const refNumberDisplayName = String(
      this.settings.referenceNumberDisplayName ??
        getExportableFieldLabel('REFERENCE_NUMBER', this.locale)
    );

    const handlingUnitsLabel = String(
      this.settings.handlingUnitsLabel ?? getExportableFieldLabel('HANDLING_UNITS', this.locale)
    );

    return sortBy(this.appointments || [], 'start').map(appointment => {
      if (!appointment.dock) {
        throw new Error(
          `Core: ExportAppointment.convertAppointmentsToExcelData(): appointment missing dock; appointmentId=${appointment.id}`
        );
      }
      const warehouseWithOrgSettings: TypeormEntity = {
        ...(appointment.dock.warehouse ?? {}),
        org: { settings: this.settings }
      };
      const consolidatedSettings = makeEntitySettings(
        DatabaseEntities.Warehouse,
        warehouseWithOrgSettings
      );

      return this.#makeRow(
        appointment,
        refNumberDisplayName,
        handlingUnitsLabel,
        consolidatedSettings
      );
    });
  }

  #calculateAppointmentFields(
    appointment: IAppointmentForExport,
    settings: ISettings,
    timezone: string
  ) {
    let completedT: Maybe<DateTime>;
    let actualDuration: Maybe<number>;
    let onTimeMinutes: Maybe<number>;

    const militaryTimeEnabled = Boolean(
      typeof settings.enableMilitaryTime === 'boolean'
        ? settings.enableMilitaryTime
        : EntitySettings.Org.enableMilitaryTime.defaultValue
    );

    const timeline = appointment.statusTimeline;
    const arrivedAtRaw = timeline?.[AppointmentStatus.Arrived];
    const arrivedT = arrivedAtRaw
      ? this.#getDateTimeObject(arrivedAtRaw, {
          zone: timezone
        }).startOf('minute')
      : null;

    if (arrivedT && timeline?.[AppointmentStatus.Completed]) {
      completedT = this.#getDateTimeObject(timeline[AppointmentStatus.Completed], {
        zone: timezone
      }).startOf('minute');
      actualDuration = completedT.diff(arrivedT, 'minutes').as('minutes');
    }

    if (arrivedT) {
      onTimeMinutes = this.#makeTimeDiff(appointment.start, arrivedT.toISO(), timezone, 'minutes');
    }

    return { completedT, arrivedT, onTimeMinutes, actualDuration, militaryTimeEnabled };
  }

  #makeRow(
    appointment: IAppointmentForExport,
    refNumberDisplayName: string,
    handlingUnitsLabel: string,
    settings: ISettings
  ) {
    const modifiedRow: Record<string, ExportAppointmentRowValue> = {};

    const timezone = appointment.dock?.warehouse?.timezone || 'UTC';
    const { militaryTimeEnabled } = this.#calculateAppointmentFields(
      appointment,
      settings,
      timezone
    );

    this.#makeAppointmentDataCells(appointment, militaryTimeEnabled, timezone, modifiedRow);
    this.#makeStatusCells(appointment, settings, timezone, modifiedRow);
    this.#makeAppointmentEntitiesCells(appointment, modifiedRow);
    if (this.exportableFields.includes('REFERENCE_NUMBER')) {
      modifiedRow[refNumberDisplayName] = appointment.refNumber ?? '';
    }
    if (this.exportableFields.includes('HANDLING_UNITS')) {
      modifiedRow[handlingUnitsLabel] = isNil(appointment.units) ? '' : appointment.units;
    }
    this.#makeCreateUpdateCells(appointment, timezone, militaryTimeEnabled, modifiedRow);
    this.#makeCarrierDataCells(appointment, modifiedRow);
    this.#makeTagsNotesCells(appointment, modifiedRow);
    this.#makeMetadataCells(appointment, modifiedRow, settings);

    if (this.exportableFields.includes('CUSTOM_FIELDS')) {
      this.#makeRowCustomFields(appointment, modifiedRow);
    }

    if (appointment.customFormData?.length) {
      this.#makeCustomFormsFields(appointment, modifiedRow);
    }

    return modifiedRow;
  }

  #makeAppointmentEntitiesCells(
    appointment: IAppointmentForExport,
    modifiedRow: Record<string, ExportAppointmentRowValue>
  ) {
    this.#makeRowCell('WAREHOUSE', appointment.dock?.warehouse?.name, modifiedRow);
    this.#makeRowCell('DOCK', appointment.dock?.name, modifiedRow);
    this.#makeRowCell('LOAD_TYPE', appointment.loadType?.name, modifiedRow);
    this.#makeRowCell('LOAD_TYPE_DIRECTION', appointment.loadType?.direction, modifiedRow);
  }

  #makeMetadataCells(
    appointment: IAppointmentForExport,
    modifiedRow: Record<string, ExportAppointmentRowValue>,
    settings: ISettings
  ) {
    if (settings.overrideExternalAppointmentValidation) {
      this.#makeExternalValidationFields(appointment, modifiedRow);
    }
  }

  #makeExternalValidationFields(
    appointment: IAppointmentForExport,
    modifiedRow: Record<string, ExportAppointmentRowValue>
  ) {
    let validationStatus = 'N/A';
    let validationErrorMessage = 'N/A';

    if (
      typeof appointment.metadata?.[AppointmentMetadataEnum.externalValidationFailed] !==
      'undefined'
    ) {
      validationStatus =
        appointment.metadata[AppointmentMetadataEnum.externalValidationFailed] === true
          ? 'Failed'
          : 'Passed';
    }

    if (appointment.metadata?.[AppointmentMetadataEnum.externalValidationErrorMessage]) {
      validationErrorMessage =
        appointment.metadata[AppointmentMetadataEnum.externalValidationErrorMessage];
    }
    this.#makeRowCell('EXTERNAL_VALIDATION_STATUS', validationStatus, modifiedRow);
    this.#makeRowCell('EXTERNAL_VALIDATION_ERROR_MESSAGE', validationErrorMessage, modifiedRow);
  }

  #makeTagsNotesCells(
    appointment: IAppointmentForExport,
    modifiedRow: Record<string, ExportAppointmentRowValue>
  ) {
    this.#makeRowCell('NOTES', this.stripNotesField(appointment.notes), modifiedRow);
    this.#makeRowCell(
      'TAGS',
      !appointment.tags ? null : appointment.tags.map((t: string) => `#${t}`).join(', '),
      modifiedRow
    );
  }

  #makeCarrierDataCells(
    appointment: IAppointmentForExport,
    modifiedRow: Record<string, ExportAppointmentRowValue>
  ) {
    if (!appointment.user) {
      return;
    }
    this.#makeRowCell('CARRIER_COMPANY', appointment.user?.company?.name, modifiedRow);
    this.#makeRowCell(
      'CONTACT_NAME',
      appointment.user ? `${appointment.user.firstName} ${appointment.user.lastName}` : null,
      modifiedRow
    );
    this.#makeRowCell('CONTACT_EMAIL', appointment.user?.email, modifiedRow);
    this.#makeRowCell('CONTACT_PHONE', appointment.user?.phone, modifiedRow);
  }

  #makeAppointmentDataCells(
    appointment: IAppointmentForExport,
    militaryTimeEnabled: boolean,
    timezone: string,
    modifiedRow: Record<string, ExportAppointmentRowValue>
  ) {
    this.#makeRowCell('CONFIRMATION_NUMBER', appointment.confirmationNumber, modifiedRow);
    this.#makeRowCell('APPOINTMENT_ID', appointment.id, modifiedRow);
    this.#makeRowCell(
      'APPOINTMENT_DATE',
      this.#getDateTimeObject(appointment.start, { zone: timezone }).toFormat(
        LuxonDateTimeFormats.MonthDayYearSlashed
      ),
      modifiedRow
    );
    this.#makeRowCell(
      'APPOINTMENT_TIME',
      formatDateTimeWithMilitarySupport(
        this.#isoDateTime(appointment.start),
        timezone,
        LuxonDateTimeFormats.Extended12HrTimeAMPM,
        militaryTimeEnabled,
        LuxonDateTimeFormats.Extended24HrTime
      ),
      modifiedRow
    );
    this.#makeRowCell(
      'APPOINTMENT_TIMEZONE',
      formatDateTimeWithMilitarySupport(
        this.#isoDateTime(appointment.start),
        timezone,
        LuxonDateTimeFormats.AbbreviatedNamedOffset,
        militaryTimeEnabled,
        LuxonDateTimeFormats.AbbreviatedNamedOffset
      ),
      modifiedRow
    );
    this.#makeRowCell('APPOINTMENT_TYPE', String(appointment.type), modifiedRow);
  }

  #makeCreateUpdateCells(
    appointment: IAppointmentForExport,
    timezone: string,
    militaryTimeEnabled: boolean,
    modifiedRow: Record<string, ExportAppointmentRowValue>
  ) {
    this.#makeRowCell('RESCHEDULE_COUNT', getReschedulesCount(appointment).toFixed(0), modifiedRow);
    if (appointment.createdByUser?.id) {
      this.#makeRowCell(
        'CREATED_BY',
        `${appointment.createdByUser.firstName} ${appointment.createdByUser.lastName}`,
        modifiedRow
      );
      this.#makeRowCell(
        'CREATOR_TYPE',
        this.#makeAppointmentCreatorType(appointment.createdByUser),
        modifiedRow
      );
    }
    this.#makeRowCell(
      'CREATION_DATE',
      this.#getDateTimeObject(appointment.createDateTime, { zone: timezone }).toFormat(
        LuxonDateTimeFormats.MonthDayYearSlashed
      ),
      modifiedRow
    );

    if (appointment.lastChangedByUser?.id) {
      this.#makeRowCell(
        'LAST_UPDATED_BY',
        `${appointment.lastChangedByUser.firstName} ${appointment.lastChangedByUser.lastName}`,
        modifiedRow
      );
    }
    this.#makeRowCell(
      'LAST_UPDATE_DATE',
      this.#getDateTimeObject(appointment.lastChangedDateTime, { zone: timezone }).toFormat(
        LuxonDateTimeFormats.MonthDayYearSlashed
      ),
      modifiedRow
    );
    this.#makeRowCell(
      'LAST_UPDATE_TIME',
      formatDateTimeWithMilitarySupport(
        this.#isoDateTime(appointment.lastChangedDateTime),
        timezone,
        LuxonDateTimeFormats.Extended12HrTimeAMPM,
        militaryTimeEnabled,
        LuxonDateTimeFormats.Extended24HrTime
      ),
      modifiedRow
    );
  }

  #makeStatusCells(
    appointment: IAppointmentForExport,
    settings: ISettings,
    timezone: string,
    modifiedRow: Record<string, ExportAppointmentRowValue>
  ) {
    const { militaryTimeEnabled, onTimeMinutes, actualDuration, completedT, arrivedT } =
      this.#calculateAppointmentFields(appointment, settings, timezone);

    this.#makeRowCell(
      'SCHEDULED_DURATION',
      this.#makeTimeDiff(appointment.start, appointment.end, timezone, 'minutes').toFixed(0),
      modifiedRow
    );
    this.#makeRowCell('STATUS', String(appointment.status), modifiedRow);
    this.#makeRowCell(
      'ARRIVAL_DATE',
      arrivedT ? arrivedT.toFormat(LuxonDateTimeFormats.MonthDayYearSlashed) : '',
      modifiedRow
    );
    this.#makeRowCell(
      'ARRIVAL_TIME',
      arrivedT
        ? formatDateTimeWithMilitarySupport(
            arrivedT.toISO(),
            timezone,
            LuxonDateTimeFormats.Extended12HrTimeAMPM,
            militaryTimeEnabled,
            LuxonDateTimeFormats.Extended24HrTime
          )
        : '',
      modifiedRow
    );
    this.#makeRowCell(
      'DEPARTURE_DATE',
      completedT ? completedT.toFormat(LuxonDateTimeFormats.MonthDayYearSlashed) : '',
      modifiedRow
    );
    this.#makeRowCell(
      'DEPARTURE_TIME',
      completedT
        ? formatDateTimeWithMilitarySupport(
            completedT.toISO(),
            timezone,
            LuxonDateTimeFormats.Extended12HrTimeAMPM,
            militaryTimeEnabled,
            LuxonDateTimeFormats.Extended24HrTime
          )
        : '',
      modifiedRow
    );
    this.#makeRowCell('DWELL_TIME', actualDuration?.toFixed(0) || '', modifiedRow);
    this.#makeRowCell('ON_TIME', onTimeMinutes?.toFixed(0) || '', modifiedRow);

    this.#makeInProgressStatusCells(
      appointment,
      settings,
      timezone,
      militaryTimeEnabled,
      modifiedRow
    );
  }

  #makeInProgressStatusCells(
    appointment: IAppointmentForExport,
    settings: ISettings,
    timezone: string,
    militaryTimeEnabled: boolean,
    modifiedRow: Record<string, ExportAppointmentRowValue>
  ) {
    let arrivedTimeStamp: Maybe<string | Date>;
    let inProgressTimeStamp: Maybe<string | Date>;
    let completedTimeStamp: Maybe<string | Date>;

    // Since the default value is Completed, we can ignore these if there
    // are no settings
    if (settings.statusSucceedingArrived === AppointmentStatus.InProgress) {
      const timeline = appointment.statusTimeline;
      if (timeline) {
        arrivedTimeStamp = timeline[AppointmentStatus.Arrived];
        inProgressTimeStamp = timeline[AppointmentStatus.InProgress];
        completedTimeStamp = timeline[AppointmentStatus.Completed];
      }
    }

    this.#makeRowCell(
      'IN_PROGRESS_DATE',
      inProgressTimeStamp
        ? this.#getDateTimeObject(inProgressTimeStamp).toFormat(
            LuxonDateTimeFormats.MonthDayYearSlashed
          )
        : '',
      modifiedRow
    );

    this.#makeRowCell(
      'IN_PROGRESS_TIME',
      inProgressTimeStamp
        ? formatDateTimeWithMilitarySupport(
            this.#isoDateTime(inProgressTimeStamp),
            timezone,
            LuxonDateTimeFormats.Extended12HrTimeAMPM,
            militaryTimeEnabled,
            LuxonDateTimeFormats.Extended24HrTime
          )
        : '',
      modifiedRow
    );

    let waitingTime = '';
    if (arrivedTimeStamp && inProgressTimeStamp) {
      waitingTime = this.#makeTimeDiff(
        arrivedTimeStamp,
        inProgressTimeStamp,
        timezone,
        'minutes'
      ).toFixed();
    }
    this.#makeRowCell('WAIT_TIME', waitingTime, modifiedRow);

    let processingTime = '';
    if (completedTimeStamp && inProgressTimeStamp) {
      processingTime = this.#makeTimeDiff(
        inProgressTimeStamp,
        completedTimeStamp,
        timezone,
        'minutes'
      ).toFixed();
    }
    this.#makeRowCell('PROCESSING_TIME', processingTime, modifiedRow);
  }

  #makeRowCell(
    key: ExportableFieldKey,
    value: ExportAppointmentRowValue | null | undefined,
    row: Record<string, ExportAppointmentRowValue>
  ) {
    if (this.exportableFields.includes(key)) {
      const header = getExportableFieldLabel(key, this.locale);
      row[header] = isNil(value) ? '' : value;
    }
  }

  #isoDateTime(value: string | Date): string {
    return new NovaDate(value).toISOString();
  }

  #makeAppointmentCreatorType(user: IUser) {
    if (!user) {
      return 'Unknown user';
    }
    return isCarrierUser(user) ? 'Carrier' : 'Warehouse';
  }

  #getDateTimeObject(date: string | Date, options?: DateTimeOptions): DateTime {
    if (isDate(date)) {
      date = date.toISOString();
    }
    return DateTime.fromISO(date, options);
  }

  #makeTimeDiff(
    start: string | Date,
    end: string | Date,
    timezone: string,
    unit: DurationUnits = 'milliseconds'
  ): number {
    const startDateTime = this.#getDateTimeObject(start, { zone: timezone }).startOf('minute');
    const endDateTime = this.#getDateTimeObject(end, { zone: timezone }).startOf('minute');
    const diffObject = endDateTime.diff(startDateTime, unit).toObject() as Record<
      string,
      Maybe<number>
    >;
    const raw = diffObject[String(unit)];
    return isNumber(raw) ? raw : 0;
  }

  #makeCustomFormsFields(
    appointment: IAppointmentForExport,
    modifiedRow: Record<string, ExportAppointmentRowValue>
  ) {
    const customFormFeatureKeys = Object.keys(CustomFormsFeatures) as Array<
      keyof typeof CustomFormsFeatures
    >;
    const customFormsRequested = this.exportableFields
      .filter((field): field is keyof typeof CustomFormsFeatures =>
        customFormFeatureKeys.includes(field as keyof typeof CustomFormsFeatures)
      )
      .map(field => CustomFormsFeatures[field]);
    const requestedFeatures = customFormsRequested as readonly string[];

    // Individual load-type custom form fields selected via `ltcf:<formFieldId>` keys.
    // These let the report include specific Book Load Type fields instead of the whole feature.
    const requestedFormFieldIds = new Set(
      this.exportableFields
        .map(field => parseGridTileLoadTypeCustomFormFieldId(field))
        .filter((formFieldId): formFieldId is string => formFieldId !== null)
    );

    if (!requestedFeatures.length && requestedFormFieldIds.size === 0) {
      return;
    }
    const formData = appointment.customFormData;
    if (!formData) {
      return;
    }
    formData
      .filter(
        (cfField: AppointmentCustomFormExportRow) =>
          requestedFeatures.includes(cfField.feature) ||
          (!isNil(cfField.formFieldId) && requestedFormFieldIds.has(cfField.formFieldId))
      )
      .forEach((cfField: AppointmentCustomFormExportRow) => {
        if (cfField.type === 'multidoc') {
          return;
        }
        let v: ExportAppointmentRowValue = cfField.value;
        if (cfField.type === 'doc') {
          const docUrl = cfField.value;
          if (docUrl) {
            v = {
              t: 's',
              v: getDocumentNameFromUrl(docUrl) ?? '',
              l: {
                Target: docUrl,
                Tooltip: 'Download File'
              } // Target is the URL, optionally specify Tooltip
            };
          }
        }
        if (cfField.type === 'bool') {
          v = cfField.value === 'true' ? 'Yes' : 'No';
        }
        modifiedRow[String(cfField.label)] = v;
      });
  }

  #makeRowCustomFields(
    appointment: IAppointmentForExport,
    modifiedRow: Record<string, ExportAppointmentRowValue>
  ) {
    if (Array.isArray(appointment.customFields)) {
      const fieldCount: Record<string, number> = {};
      appointment.customFields.forEach((cField: ICustomField) => {
        let v: ExportAppointmentRowValue = '';

        if (Array.isArray(cField.value)) {
          v = cField.value.join(', ');
        } else if (
          cField.type === CustomFieldType.Number &&
          !isNil(cField.value) &&
          String(cField.value) !== ''
        ) {
          v = parseInt(String(cField.value), 10);
        } else if (
          cField.type === CustomFieldType.Document &&
          typeof cField.value === 'string' &&
          cField.value
        ) {
          const docUrl = cField.value;
          v = {
            t: 's',
            v: getDocumentNameFromUrl(docUrl) ?? '',
            l: {
              Target: docUrl,
              Tooltip: 'Download File'
            }
          };
        } else if (isBoolean(cField.value)) {
          v = cField.value ? 'Yes' : 'No';
        } else if (!isNil(cField.value)) {
          v = String(cField.value);
        }

        if (modifiedRow[cField.label]) {
          fieldCount[cField.label] = (fieldCount[cField.label] || 1) + 1;
          modifiedRow[`${cField.label} (${fieldCount[cField.label]})`] = v;
        } else {
          fieldCount[cField.label] = 1;
          modifiedRow[cField.label] = v;
        }
      });
    }
  }
}
